import { VisitAttendance } from './../../../../core/models/expense.model';
import { Permissions } from './../../../../core/constants/permissions';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { Lightbox } from "ngx-lightbox";
import { DropdownInputComponent } from "../../../../shared/dropdown-input/dropdown-input.component";
import {
  ApproveExpenseClaimResult,
  CaxtonIncompatibleData,
  ExpenseCategoryType,
  ExpenseClaimPaidBy
} from "../../../../core/models/expense.model";
import { LogHelper } from "../../../../core/helpers/log.helper";
import { PatientService } from "../../../../core/services/patient.service";
import { AuthService } from "../../../../core/services/auth.service";
import { AlertService } from "../../../../shared/alert/alert.service";
import { ExpenseService } from "../../../../core/services/expense.service";
import { PatientExpenseListItem, PatientExpensesList } from "../../../../core/models/patient-expenses-list.model";
import { TemplateService } from "../../../../core/services/template.service";
import { ModalComponent } from "../../../../shared/modal/modal.component";
import { StringHelper } from "../../../../core/helpers/string-helper";
import { EditExpenseModalComponent } from '../../../expense/edit-expense-modal/edit-expense-modal.component';
import { CreateExpenseModalComponent } from '../../../expense/create-expense-modal/create-expense-modal.component';
import { Currencies } from '../../../../core/constants/currency';
import {
  CloseInvestigationModalComponent
} from '../../../expense/close-investigation-modal/close-investigation-modal.component';
import {
  StartInvestigationModalComponent
} from '../../../expense/start-investigation-modal/start-investigation-modal.component';
import { OverBudgetRequestDetailsStatus } from '../../../../core/models/project-management.model';
import { RoleNames } from '../../../../core/constants/role-names';
import { requiredNoWhitespaceValidator } from "../../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-patient-detail-expenses',
  templateUrl: './patient-detail-expenses.component.html',
  styleUrls: ['./patient-detail-expenses.component.scss']
})
export class PatientDetailExpensesComponent implements OnInit {
  @ViewChild('createExpenseModal') createExpenseModal: CreateExpenseModalComponent;
  @ViewChild('editExpenseModal') editExpenseModal: EditExpenseModalComponent;
  @ViewChild('rejectExpenseModal') rejectExpenseModal: ModalComponent;
  @ViewChild('approveDistanceModal') approveDistanceModal: ModalComponent;
  @ViewChild('currencySelect') currencySelect: DropdownInputComponent;
  @ViewChild('startInvestigationModal') startInvestigationModal: StartInvestigationModalComponent;
  @ViewChild('closeInvestigationModal') closeInvestigationModal: CloseInvestigationModalComponent;
  @ViewChild('confirmExpenseApprove') confirmExpenseApproveModal: ModalComponent;
  @Input() patientTrialId: string;
  @Input() patientId: string;
  @Input() trialAllowsExpenses = false;

  rejectForm: UntypedFormGroup;
  approveDistanceForm: UntypedFormGroup;
  results: PatientExpensesList = new PatientExpensesList();
  approveFormIsProcessing = false;
  rejectFormIsProcessing = false;
  exportIsProcessing = false;
  stringHelper = StringHelper;
  VisitAttendance = VisitAttendance;

  investigationFormProcessing = false;
  investigationForm: UntypedFormGroup;

  currencyOptions: { value: string, text: string }[] = [];
  ExpenseClaimPaidBy = ExpenseClaimPaidBy;
  caxtonIncompatibleData: CaxtonIncompatibleData;
  confirmExpenseClaimApprovalProcessing = false;
  confirmExpenseApproveOnComplete: () => void;
  Permissions = Permissions;
  ExpenseCategoryType = ExpenseCategoryType;
  OverBudgetRequestDetailsStatus = OverBudgetRequestDetailsStatus;
  page: number;
  processingExpenseIds = new Set<string>();

  constructor(public authService: AuthService, private lightbox: Lightbox, private templateService: TemplateService, private alertService: AlertService, private activatedRoute: ActivatedRoute, private router: Router, private patientService: PatientService, private expenseService: ExpenseService) { }

  ngOnInit() {
    this.loadPatientExpenses(1);

    for (const currency of Currencies.all()) {
      this.currencyOptions.push({ value: currency.cc, text: currency.cc });
    }

    this.rejectForm = new UntypedFormGroup({
      expenseId: new UntypedFormControl('', Validators.required),
      reason: new UntypedFormControl('', requiredNoWhitespaceValidator())
    });

    this.approveDistanceForm = new UntypedFormGroup({
      expenseId: new UntypedFormControl('', Validators.required),
      currency: new UntypedFormControl('', Validators.required),
      amount: new UntypedFormControl('', Validators.required)
    });

    this.investigationForm = new UntypedFormGroup({
      id: new UntypedFormControl('', Validators.required),
      reasonForInvestigation: new UntypedFormControl('', [requiredNoWhitespaceValidator(), Validators.maxLength(250)])
    });
  }

  viewReceipt(imageUrl: string) {
    const album: { src: string, caption: string, thumb: string, downloadUrl: string }[] = [];
    album.push({ src: imageUrl, caption: '', thumb: imageUrl, downloadUrl: '' });

    this.lightbox.open(album, 0);
  }

  onApproveDistanceExpenseClaim() {
    this.approveFormIsProcessing = true;
    let expenseId = this.approveDistanceForm.get('expenseId').value;
    let currency = this.currencySelect.selectedValue;
    let amount = this.approveDistanceForm.get('amount').value;

    if (this.approveDistanceForm.valid) {
      this.processingExpenseIds.add(expenseId);

      this.expenseService.approveExpenseClaim(expenseId, this.caxtonIncompatibleData?.caxtonIncompatibleReason, currency, amount).subscribe({
        next: result => {
          this.approveFormIsProcessing = false;
          this.confirmExpenseClaimApprovalProcessing = false;
          this.approveDistanceModal.hide();

          this.handleApproveExpenseResponse(result, expenseId, ExpenseCategoryType.Distance, () => this.processingExpenseIds.delete(expenseId));
        },
        error: error => {
          this.processingExpenseIds.delete(expenseId);
          this.alertService.showWarningAlert('Unable to approve expense claim!');
          this.approveFormIsProcessing = false;
          this.confirmExpenseClaimApprovalProcessing = false;
        }
      })
    }
  }

  onApproveExpenseClaim(expenseId: string, type: ExpenseCategoryType) {
    if (this.processingExpenseIds.has(expenseId)) {
      return;
    }

    if (type === ExpenseCategoryType.Distance) {
      this.currencySelect.reset();

      let expense = this.results.results.find(e => e.id == expenseId);

      this.approveDistanceForm.patchValue({
        expenseId: expenseId,
        currency: expense?.currency,
        amount: expense?.amount
      });

      if (expense) {
        this.currencySelect.setValue(expense.currency);
      }

      this.approveDistanceModal.show();
    } else {
      this.processingExpenseIds.add(expenseId);

      this.expenseService.approveExpenseClaim(expenseId, null, null, null).subscribe({
        next: result => {
          this.handleApproveExpenseResponse(result, expenseId, type, () => this.processingExpenseIds.delete(expenseId));
        },
        error: error => {
          this.processingExpenseIds.delete(expenseId);
          LogHelper.log(error);
          const errorMessage = error.error.title ? error.error.title : 'Unable to approve expense claim!';
          this.alertService.showWarningAlert(errorMessage);
        }
      })
    }
  }

  /**
   * Called when the user selects to edit a patient expense
   * @param expenseId
   */
  onEditExpense(expenseId: string) {
    this.editExpenseModal.show(expenseId);
  }

  /**
   * Called when an expense claim has been updated, will refresh the current list of expenses
   */
  reloadExpenses() {
    this.loadPatientExpenses(this.results.currentPage);
  }

  confirmExpenseClaimApproval() {
    this.confirmExpenseClaimApprovalProcessing = true;

    if (this.caxtonIncompatibleData.type === ExpenseCategoryType.Distance) {
      this.onApproveDistanceExpenseClaim();
    } else {
      var onComplete = this.confirmExpenseApproveOnComplete;
      this.confirmExpenseApproveOnComplete = null;

      this.expenseService.approveExpenseClaim(this.caxtonIncompatibleData.expenseClaimId, this.caxtonIncompatibleData.caxtonIncompatibleReason, null, null).subscribe({
        next: result => {
          this.handleApproveExpenseResponse(result, this.caxtonIncompatibleData.expenseClaimId, this.caxtonIncompatibleData.type, onComplete);
        },
        error: error => {
          LogHelper.log(error);
          this.alertService.showWarningAlert('Unable to approve expense claim!');

          if (onComplete)
          {
            onComplete();
          }
        },
        complete: () => {
          this.confirmExpenseClaimApprovalProcessing = false;
        }
      })
    }
  }

  handleApproveExpenseResponse(result: ApproveExpenseClaimResult, expenseId: string, type: ExpenseCategoryType, onComplete?: () => void) {
    if (!result.approved) {
      this.caxtonIncompatibleData = {
        caxtonIncompatibleReason: result.caxtonIncompatibleMessage,
        expenseClaimId: expenseId,
        type: type
      }

      this.confirmExpenseApproveOnComplete = onComplete;
      this.confirmExpenseApproveModal.show();
      return;
    }

    this.caxtonIncompatibleData = null;
    this.alertService.showSuccessAlert('Expense Claim Successfully Approved.');
    this.loadPatientExpenses(this.results.currentPage, onComplete);
    this.confirmExpenseApproveModal.hide();
  }

  loadPatientExpenses(page: number, onComplete?: () => void) {
    this.page = page;
    this.patientService.retrievePatientExpensesForPatientTrial(this.patientTrialId, page).subscribe({
      next: patientList => {
        console.log(patientList);
        this.results = patientList;

        if (onComplete)
        {
          onComplete();
        }
      },
      error: error => {
        LogHelper.log(error);
        this.alertService.showWarningAlert('Unable to load patient expenses!');

        if (onComplete)
        {
          onComplete();
        }
      }
    });
  }

  onCurrencySelectChanged(value: string) {
    this.approveDistanceForm.patchValue({ currency: value });
  }

  onConfirmRejectExpense() {
    this.rejectFormIsProcessing = true;
    this.expenseService.rejectExpenseClaim(this.rejectForm.get('expenseId').value, this.rejectForm.get('reason').value).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('Expense Claim Successfully Rejected.');
        this.rejectExpenseModal.hide();
        this.loadPatientExpenses(this.results.currentPage);
        this.rejectFormIsProcessing = false;
      },
      error: error => {
        LogHelper.log(error);
        this.alertService.showWarningAlert('Unable to reject expense claim!');
        this.rejectExpenseModal.hide();
      }
    });
  }

  onRejectModal(expenseId: string) {
    this.rejectForm.patchValue({
      expenseId: expenseId,
      reason: ''
    });
    this.rejectExpenseModal.show();
  }

  onMarkAsPaid(expenseId: string) {
    if (this.processingExpenseIds.has(expenseId)) {
      return;
    }

    this.processingExpenseIds.add(expenseId);

    this.expenseService.markExpenseClaimAsPaid(expenseId).subscribe({
      next: () => {
        this.loadPatientExpenses(this.results.currentPage, () => this.processingExpenseIds.delete(expenseId));
      },
      error: error => {
        this.processingExpenseIds.delete(expenseId);
        LogHelper.log(error);
        this.alertService.showWarningAlert('Sorry, there was a problem!');
      }
    });
  }

  isExpenseProcessing(expenseId: string): boolean {
    return this.processingExpenseIds.has(expenseId);
  }

  onCloseInvestigation(claimId: string) {
    this.closeInvestigationModal.expenseClaimId = claimId;
    this.closeInvestigationModal.modal.show();
  }

  onStartInvestigation(claimId: string) {
    this.startInvestigationModal.expenseClaimId = claimId;
    this.startInvestigationModal.modal.show();
  }

  getBadgeLabel(expenseState: string) {
    switch (expenseState) {
      case 'AutomatedPaymentApproved':
        return 'Automated Payment Approved';
      case 'Processing':
        return 'Payment Processing';
      case 'AutomatedPaymentFailed':
        return 'Automated Payment Failed';
      default:
        return expenseState;
    }
  }

  closeConfirmExpenseApproveModal() {
    this.caxtonIncompatibleData = null;
    this.confirmExpenseApproveModal.hide();

    if (this.confirmExpenseApproveOnComplete)
    {
      this.confirmExpenseApproveOnComplete();
      this.confirmExpenseApproveOnComplete = null;
    }
  }

  public uploadIsPdf(uploadUrl: string) {
    let parts = uploadUrl.toLowerCase().split('?');
    return parts[0].endsWith('.pdf');
  }

  onExportPatientExpenses(patientId) {
    this.exportIsProcessing = true;
    this.patientService.exportPatientExpenses(patientId).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('The export request was sent, please check your email.');

        this.exportIsProcessing = false;
      },
      error: error => {
        LogHelper.log(error);
        this.exportIsProcessing = false;
        this.alertService.showWarningAlert('There was a problem requesting the export.');
      }
    });
  }

  readonly canCreateExpense = this.authService.hasAnyRole([
    RoleNames.ProjectCoordinatorTravel,
    RoleNames.ProjectCoordinatorExpenses,
    RoleNames.ProductAdministrator,
    RoleNames.CallCentre
  ].join(','))

  protected readonly RoleNames = RoleNames;

  hasAiReceiptData(expense: PatientExpenseListItem): boolean {
    return expense.receipts?.length > 0 && expense.receipts.some(r => r.amount != null);
  }

  receiptCategoryMatches(expense: PatientExpenseListItem): boolean {
    if (!this.hasAiReceiptData(expense)) return false;
    return expense.receipts
      .filter(r => r.amount != null && r.categoryName)
      .every(r => r.categoryName === expense.categoryName);
  }

  categoryMismatchReason(expense: PatientExpenseListItem): string {
    const categories = [...new Set(
      expense.receipts.filter(r => r.amount != null && r.categoryName).map(r => r.categoryName)
    )];
    return `AI categorised receipt as "${categories.join(', ')}" but claim category is "${expense.categoryName}"`;
  }

  receiptTotal(expense: PatientExpenseListItem): number {
    if (!this.hasAiReceiptData(expense)) return 0;
    return expense.receipts.filter(r => r.amount != null).reduce((sum, r) => sum + r.amount, 0);
  }

  receiptCurrencyMatches(expense: PatientExpenseListItem): boolean {
    if (!this.hasAiReceiptData(expense)) return false;
    return expense.receipts
      .filter(r => r.amount != null && r.currency)
      .every(r => r.currency === expense.currency);
  }

  receiptAmountMatches(expense: PatientExpenseListItem): boolean {
    return this.hasAiReceiptData(expense) &&
      Math.round(this.receiptTotal(expense) * 100) === Math.round(expense.amount * 100) &&
      this.receiptCurrencyMatches(expense);
  }

  receiptMismatchReason(expense: PatientExpenseListItem): string {
    const reasons: string[] = [];
    const processedReceipts = expense.receipts.filter(r => r.amount != null);

    if (!this.receiptCurrencyMatches(expense)) {
      const currencies = [...new Set(processedReceipts.filter(r => r.currency).map(r => r.currency))];
      reasons.push(`Receipt currency (${currencies.join(', ')}) does not match claim currency (${expense.currency})`);
    }

    const total = this.receiptTotal(expense);
    if (Math.round(total * 100) !== Math.round(expense.amount * 100)) {
      reasons.push(`Receipt total (${total.toFixed(2)}) does not match claim amount (${expense.amount.toFixed(2)})`);
    }

    return reasons.join('. ');
  }
}
